<template>
  <themed-modal
    name="create-building-wizard"
    title="Create New Building Office"
    size="lg"
    :show-footer="true"
    @closed="handleClose"
  >
    <!-- Content - Single Scrollable Form -->
    <div class="create-building-wizard__content">
        <!-- Building and Unit Side-by-Side -->
        <div class="create-building-wizard__top-row">
          <!-- Building Section -->
          <div class="create-building-wizard__section create-building-wizard__section--building">
            <h3 class="create-building-wizard__section-title">BUILDING</h3>
            
            <div class="create-building-wizard__form">
              <div class="create-building-wizard__field">
                <label class="create-building-wizard__label">Address</label>
                <themed-input
                  v-model="building.address_line_1"
                  @input="resetAddressValidation"
                />
                <div v-if="errors.address_line_1" class="create-building-wizard__error">
                  {{ errors.address_line_1[0] }}
                </div>
              </div>

              <div class="create-building-wizard__field">
                <label class="create-building-wizard__label">City</label>
                <themed-input
                  v-model="building.city"
                  @input="resetAddressValidation"
                />
                <div v-if="errors.city" class="create-building-wizard__error">
                  {{ errors.city[0] }}
                </div>
              </div>

              <div class="create-building-wizard__field-row">
                <div class="create-building-wizard__field create-building-wizard__field--state">
                  <label class="create-building-wizard__label">State</label>
                  <themed-select
                    v-if="building.country_id == 16"
                    v-model="building.state"
                    :options="states.map(s => ({ value: s.state, label: s.state }))"
                    @input="resetAddressValidation"
                  />
                  <themed-input
                    v-else
                    v-model="building.state"
                    @input="resetAddressValidation"
                  />
                  <div v-if="errors.state" class="create-building-wizard__error">
                    {{ errors.state[0] }}
                  </div>
                </div>
                <div class="create-building-wizard__field create-building-wizard__field--postal">
                  <label class="create-building-wizard__label">Postal</label>
                  <themed-input
                    v-model="building.postal_code"
                    @input="resetAddressValidation"
                  />
                  <div v-if="errors.postal_code" class="create-building-wizard__error">
                    {{ errors.postal_code[0] }}
                  </div>
                </div>
                <div class="create-building-wizard__field create-building-wizard__field--country">
                  <label class="create-building-wizard__label">Cntry</label>
                  <themed-select
                    v-model="building.country_id"
                    :options="countries.map(c => ({ value: c.id, label: c.code || c.name }))"
                    @input="resetAddressValidation"
                  />
                  <div v-if="errors.country_id" class="create-building-wizard__error">
                    {{ errors.country_id[0] }}
                  </div>
                </div>
              </div>

              <div v-if="addressVerified" class="create-building-wizard__verified">
                <theme-icon icon="check" type="icons" />
                <span>Address Verified</span>
              </div>
            </div>
          </div>

          <!-- Unit Section -->
          <div class="create-building-wizard__section create-building-wizard__section--unit">
            <h3 class="create-building-wizard__section-title">UNIT</h3>
            
            <div class="create-building-wizard__form">
              <div class="create-building-wizard__field-row">
                <div class="create-building-wizard__field">
                  <label class="create-building-wizard__label">Floor</label>
                  <themed-number-input
                    v-model="unit.floor"
                    name="floor"
                  />
                  <div v-if="errors.floor" class="create-building-wizard__error">
                    {{ errors.floor[0] }}
                  </div>
                </div>
                <div class="create-building-wizard__field">
                  <label class="create-building-wizard__label">Suite</label>
                  <themed-input
                    v-model="unit.suite"
                    name="suite"
                  />
                  <div v-if="errors.suite" class="create-building-wizard__error">
                    {{ errors.suite[0] }}
                  </div>
                </div>
              </div>

              <div class="create-building-wizard__field-row">
                <div class="create-building-wizard__field">
                  <label class="create-building-wizard__label">Size</label>
                  <themed-number-input
                    v-model="unit.size"
                    name="size"
                  />
                  <div v-if="errors.size" class="create-building-wizard__error">
                    {{ errors.size[0] }}
                  </div>
                </div>
                <div class="create-building-wizard__field">
                  <label class="create-building-wizard__label">Divisible</label>
                  <themed-number-input
                    v-model="unit.divisible"
                    name="divisible"
                  />
                </div>
              </div>

              <div class="create-building-wizard__field-row">
                <div class="create-building-wizard__field">
                  <label class="create-building-wizard__label">TIA $</label>
                  <themed-number-input
                    v-model="unit.ti_allowance"
                    name="ti_allowance"
                    :is-decimal="true"
                  />
                </div>
                <div class="create-building-wizard__field">
                  <label class="create-building-wizard__label">Status</label>
                  <themed-select
                    v-model="unit.status"
                    :options="[
                      { value: 'Available', label: 'Available' },
                      { value: 'Occupied', label: 'Occupied' },
                      { value: 'Sublease', label: 'Sublease' },
                      { value: 'Vacant', label: 'Vacant' }
                    ]"
                    name="status"
                  />
                  <div v-if="errors.status" class="create-building-wizard__error">
                    {{ errors.status[0] }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Property Type Section -->
        <div class="create-building-wizard__section">
          
          <div class="create-building-wizard__types">
            <button
              v-for="type in buildingTypes"
              :key="type.id"
              @click="building.type_id = type.id"
              class="create-building-wizard__type-card"
              :class="{ 'create-building-wizard__type-card--selected': building.type_id === type.id }"
            >
              <div 
                v-if="building.type_id === type.id"
                class="create-building-wizard__type-check"
              >
                <theme-icon icon="check-large" type="icons" />
              </div>
              <div class="create-building-wizard__type-icon">
                <theme-icon :icon="type.svg_name || 'building'" type="icons" />
              </div>
              <span class="create-building-wizard__type-label">{{ type.type }}</span>
            </button>
          </div>
        </div>

        <!-- Building Details Section -->
        <div v-if="building.type_id && buildingFields.length" class="create-building-wizard__section">
         
          <div class="create-building-wizard__details-layout">
            <!-- Left side: Non-boolean fields -->
            <div class="create-building-wizard__details-grid">
              <div 
                v-for="field in nonBooleanFields" 
                :key="field.field"
                class="create-building-wizard__detail-field"
              >
                <label class="create-building-wizard__detail-label">{{ field.label }}</label>
                <themed-input
                  v-if="field.type === 'text'"
                  v-model="building[field.field]"
                  :name="field.field"
                />
                <themed-number-input
                  v-else-if="field.type === 'integer' || field.type === 'double'"
                  v-model="building[field.field]"
                  :name="field.field"
                  :is-decimal="field.type === 'double'"
                />
                <themed-select
                  v-else-if="field.type === 'value-list'"
                  v-model="building[field.field]"
                  :options="field.options.map(opt => ({ value: opt.option, label: opt.option }))"
                  :name="field.field"
                />
                <themed-input
                  v-else
                  v-model="building[field.field]"
                  :name="field.field"
                />
              </div>
            </div>

            <!-- Right side: Boolean toggles -->
            <div v-if="booleanFields.length" class="create-building-wizard__toggles">
              <div 
                v-for="field in booleanFields" 
                :key="field.field"
                class="create-building-wizard__toggle-field"
              >
                <label class="create-building-wizard__toggle-label">{{ field.label }}</label>
                <themed-toggle
                  v-model="building[field.field]"
                  :name="field.field"
                />
              </div>
            </div>
          </div>
        </div>

    </div>

    <!-- Footer -->
    <template #footer>
      <div class="create-building-wizard__footer">
        <themed-button
          variant="outline"
          @click="handleClose"
        >
          Cancel
        </themed-button>

        <themed-button
          @click="createBuilding"
          :loading="loading"
          :disabled="!canCreate"
          class="create-building-wizard__create-btn"
        >
          {{ loading ? 'Creating...' : 'Create Building' }}
        </themed-button>
      </div>
    </template>
  </themed-modal>
</template>

<script>
import { mapState } from 'vuex';
import ThemedModal from '../ThemedModal.vue';
import ThemedButton from '../ThemedButton.vue';
import ThemedInput from '../ThemedInput.vue';
import ThemedSelect from '../ThemedSelect.vue';
import ThemedToggle from '../ThemedToggle.vue';
import ThemedNumberInput from '../form/ThemedNumberInput.vue';

export default {
  name: 'CreateBuildingWizard',
  
  components: {
    ThemedModal,
    ThemedButton,
    ThemedInput,
    ThemedSelect,
    ThemedToggle,
    ThemedNumberInput,
  },
  
  props: {
    isModal: {
      type: Boolean,
      default: false
    },
    forSite: {
      type: Boolean,
      default: false
    }
  },
  
  data() {
    return {
      loading: false,
      errors: {},
      countries: [],
      states: [],
      buildingTypes: [],
      building: {
        type_id: null,
        country_id: 16,
        address_line_1: '',
        city: '',
        state: '',
        postal_code: ''
      },
      unit: {
        floor: null,
        suite: '',
        status: null,
        size: 0,
        divisible: 0,
        is_renewal_space: 0,
        ti_allowance: 0,
        comment: ''
      },
      addressVerified: false
    };
  },
  
  computed: {
    ...mapState(['buildings']),
    
    buildingFields() {
      if (this.building.type_id != null) {
        const buildingType = _.find(this.buildingTypes, ['id', this.building.type_id]);
        return buildingType ? buildingType.fields : [];
      }
      return [];
    },
    
    nonBooleanFields() {
      return this.buildingFields.filter(f => f.type !== 'boolean');
    },
    
    booleanFields() {
      return this.buildingFields.filter(f => f.type === 'boolean');
    },
    
    buildingTypeName() {
      const buildingType = _.find(this.buildingTypes, ['id', this.building.type_id]);
      return buildingType ? buildingType.type : null;
    },
    
    canCreate() {
      return this.building.address_line_1 && 
             this.building.city && 
             this.building.state && 
             this.building.postal_code && 
             this.building.type_id != null && 
             this.unit.floor != null && 
             this.unit.status != null && 
             this.unit.size != 0;
    }
  },
  
  watch: {
    'building.type_id'(newTypeId) {
      if (newTypeId != null) {
        // Initialize all fields for the selected building type
        const buildingType = _.find(this.buildingTypes, ['id', newTypeId]);
        if (buildingType && buildingType.fields) {
          buildingType.fields.forEach(field => {
            // Initialize boolean fields to false if undefined
            if (field.type === 'boolean' && this.building[field.field] === undefined) {
              this.$set(this.building, field.field, false);
            }
            // Initialize other fields if undefined
            else if (this.building[field.field] === undefined) {
              if (field.type === 'integer' || field.type === 'double') {
                this.$set(this.building, field.field, 0);
              } else {
                this.$set(this.building, field.field, '');
              }
            }
          });
        }
      }
    }
  },
  
  mounted() {
    this.fetchAddressInfo();
    this.fetchBuildingTypes();
  },
  
  methods: {
    async fetchAddressInfo() {
      try {
        const response = await axios.get('/api/address-info');
        this.countries = response.data.countries;
        this.states = response.data.states;
      } catch (error) {
        console.error('Error loading address info:', error);
      }
    },
    
    async fetchBuildingTypes() {
      try {
        const response = await axios.get('/api/building-types');
        this.buildingTypes = response.data.types;
      } catch (error) {
        console.error('Error loading building types:', error);
      }
    },
    
    resetAddressValidation() {
      this.addressVerified = false;
    },
    
    getAddressComponent(components, type, shortName = false) {
      if (!components[type]) return null;
      const component = components[type];
      return shortName ? component.short_name : component.long_name;
    },
    

    async createBuilding() {
      this.loading = true;
      this.errors = {};
      
      try {
        // Step 1: Verify address if not already verified
        if (!this.addressVerified) {
          try {
            const validateResponse = await axios.post('/api/validate-building', this.building);
            
            if (!validateResponse.data.success) {
              this.$notify({
                type: 'error',
                title: 'Invalid Address',
                text: validateResponse.data.message || 'Please enter a valid address.'
              });
              this.loading = false;
              return;
            }
            
            const data = validateResponse.data.data;
            const components = data.address_components || {};
            const location = data.location || {};
            
            // Extract address components
            const streetNumber = this.getAddressComponent(components, 'street_number');
            const route = this.getAddressComponent(components, 'route');
            const city = this.getAddressComponent(components, 'locality') || 
                         this.getAddressComponent(components, 'administrative_area_level_2');
            const state = this.getAddressComponent(components, 'administrative_area_level_1', true);
            const postalCode = this.getAddressComponent(components, 'postal_code');
            
            // Validate required components
            if (!route || !city || !state || !postalCode || !location.lat || !location.lng) {
              this.$notify({
                type: 'error',
                title: 'Invalid Address',
                text: 'Google Maps could not verify this address. Please enter a valid street address.'
              });
              this.loading = false;
              return;
            }
            
            // Update building with verified data
            this.building.address_line_1 = [streetNumber, route].filter(Boolean).join(' ');
            this.building.city = city;
            this.building.state = state;
            this.building.postal_code = postalCode;
            this.building.latitude = location.lat;
            this.building.longitude = location.lng;
            this.building.formatted_address = data.formatted_address;
            this.building.place_id = data.place_id;
            this.addressVerified = true;
            
          } catch (validationError) {
            // Handle validation errors separately to avoid duplicate notifications
            const message = validationError.response?.data?.message || 'Could not validate address';
            this.$notify({
              type: 'error',
              title: 'Invalid Address',
              text: message
            });
            this.loading = false;
            return;
          }
        }
        
        // Step 2: Create building
        const response = await axios.post('/buildings', this.building);
        
        // Step 3: Create unit
        this.unit.building_id = response.data.building.id;
        await axios.post('/units', this.unit);
        
        // Step 4: Show success and redirect
        this.$notify({
          type: 'success',
          title: 'Success',
          text: 'Building created successfully!'
        });
        
        setTimeout(() => {
          if (this.forSite) {
            this.$emit('buildingCreated', response.data.building);
            this.$modal.hide('create-building-wizard');
          } else {
            window.location = '/buildings/' + response.data.building.id + '/edit';
          }
        }, 1000);
        
      } catch (error) {
        // Only handle building creation errors here
        this.$notify({
          type: 'error',
          title: 'Error',
          text: error.response?.data?.message || 'Failed to create building'
        });
      } finally {
        this.loading = false;
      }
    },
    

    handleClose() {
      if (this.isModal) {
        this.$modal.hide('create-building-wizard');
      } else {
        this.$emit('close');
      }
    }
  }
};
</script>
